export interface Airport {
  code: string;
  city: string;
  name: string;
  country: string;
  /** True dla wpisów reprezentujących całe miasto wieloportowe (IATA metropolitan area code, np. LON, PAR, NYC). */
  isMetro?: boolean;
  /**
   * True dla syntetycznych wpisów reprezentujących CAŁY KRAJ (np. „Polska — cały kraj").
   * Kod jest niestandardowy: prefix `X` + 2-litery (XPL, XGB, XFR…), żeby nie kolidować
   * z prawdziwymi IATA. Backend rozwija to przez `expandMetro` do listy wszystkich
   * fizycznych lotnisk w kraju i robi fan-out zapytań do eSky.
   */
  isCountry?: boolean;
  /** Dla `isMetro` / `isCountry` — lista kodów IATA lotnisk wchodzących w skład miasta lub kraju. */
  members?: string[];
  /** Dodatkowe warianty pisowni miasta (np. „London", „New York", „Tokyo"). */
  aliases?: string[];
}

/** Normalizacja do wyszukiwania: lower-case + strip diakrytyków + usuń niealfanumeryczne. */
export function normalizeQuery(s: string): string {
  return s
    .toLowerCase()
    // „ł"/"Ł" nie mają rozkładu NFD (to osobna litera łacińska, nie l+diakrytyk),
    // więc musimy je zmapować ręcznie zanim zdejmiemy combining marks.
    .replace(/ł/g, "l")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

// Oficjalne IATA metropolitan area codes (LON, PAR, NYC, MIL, ROM, TYO…) —
// to NIE są nowe „kody lotnisk", tylko istniejący od dekad standard używany
// przez eSky, Skyscanner, Kayak itd. do grupowania lotnisk jednego miasta.
// Wpisy umieszczamy na początku każdego bloku kraju, żeby `searchAirports`
// pokazywało je pierwsze przy dopasowaniu do nazwy miasta.
export const AIRPORTS: Airport[] = [
  // Polska
  { code: "WAR", city: "Warszawa", name: "Wszystkie lotniska", country: "Polska", isMetro: true, members: ["WAW","WMI"], aliases: ["Warsaw"] },
  { code: "WAW", city: "Warszawa", name: "Chopina", country: "Polska" },
  { code: "WMI", city: "Warszawa", name: "Modlin", country: "Polska" },
  { code: "KRK", city: "Kraków", name: "Balice", country: "Polska" },
  { code: "GDN", city: "Gdańsk", name: "Lecha Wałęsy", country: "Polska" },
  { code: "WRO", city: "Wrocław", name: "Strachowice", country: "Polska" },
  { code: "KTW", city: "Katowice", name: "Pyrzowice", country: "Polska" },
  { code: "POZ", city: "Poznań", name: "Ławica", country: "Polska" },
  { code: "SZZ", city: "Szczecin", name: "Goleniów", country: "Polska" },
  { code: "RZE", city: "Rzeszów", name: "Jasionka", country: "Polska" },
  { code: "LCJ", city: "Łódź", name: "Lublinek", country: "Polska" },
  { code: "BZG", city: "Bydgoszcz", name: "Szwederowo", country: "Polska" },
  { code: "LUZ", city: "Lublin", name: "Świdnik", country: "Polska" },
  { code: "SZY", city: "Olsztyn", name: "Mazury", country: "Polska" },
  { code: "RDO", city: "Radom", name: "Sadków", country: "Polska" },
  { code: "IEG", city: "Zielona Góra", name: "Babimost", country: "Polska" },

  // Wielka Brytania
  { code: "LON", city: "Londyn", name: "Wszystkie lotniska", country: "Wielka Brytania", isMetro: true, members: ["LHR","LGW","STN","LTN","LCY"], aliases: ["London"] },
  { code: "LHR", city: "Londyn", name: "Heathrow", country: "Wielka Brytania" },
  { code: "LGW", city: "Londyn", name: "Gatwick", country: "Wielka Brytania" },
  { code: "STN", city: "Londyn", name: "Stansted", country: "Wielka Brytania" },
  { code: "LTN", city: "Londyn", name: "Luton", country: "Wielka Brytania" },
  { code: "LCY", city: "Londyn", name: "City", country: "Wielka Brytania" },
  { code: "MAN", city: "Manchester", name: "Manchester", country: "Wielka Brytania" },
  { code: "EDI", city: "Edynburg", name: "Edinburgh", country: "Wielka Brytania" },
  { code: "GLA", city: "Glasgow", name: "Glasgow", country: "Wielka Brytania" },
  { code: "BHX", city: "Birmingham", name: "Birmingham", country: "Wielka Brytania" },
  { code: "BRS", city: "Bristol", name: "Bristol", country: "Wielka Brytania" },
  { code: "LPL", city: "Liverpool", name: "John Lennon", country: "Wielka Brytania" },
  { code: "NCL", city: "Newcastle", name: "Newcastle", country: "Wielka Brytania" },
  { code: "LBA", city: "Leeds", name: "Leeds Bradford", country: "Wielka Brytania" },
  { code: "BFS", city: "Belfast", name: "Belfast Intl", country: "Wielka Brytania" },

  // Irlandia
  { code: "DUB", city: "Dublin", name: "Dublin", country: "Irlandia" },
  { code: "ORK", city: "Cork", name: "Cork", country: "Irlandia" },
  { code: "SNN", city: "Shannon", name: "Shannon", country: "Irlandia" },

  // Francja
  { code: "PAR", city: "Paryż", name: "Wszystkie lotniska", country: "Francja", isMetro: true, members: ["CDG","ORY","BVA"], aliases: ["Paris"] },
  { code: "CDG", city: "Paryż", name: "Charles de Gaulle", country: "Francja" },
  { code: "ORY", city: "Paryż", name: "Orly", country: "Francja" },
  { code: "BVA", city: "Paryż", name: "Beauvais", country: "Francja" },
  { code: "NCE", city: "Nicea", name: "Côte d'Azur", country: "Francja" },
  { code: "LYS", city: "Lyon", name: "Saint-Exupéry", country: "Francja" },
  { code: "MRS", city: "Marsylia", name: "Provence", country: "Francja" },
  { code: "TLS", city: "Tuluza", name: "Blagnac", country: "Francja" },
  { code: "BOD", city: "Bordeaux", name: "Mérignac", country: "Francja" },
  { code: "NTE", city: "Nantes", name: "Atlantique", country: "Francja" },
  { code: "MPL", city: "Montpellier", name: "Méditerranée", country: "Francja" },
  { code: "BIQ", city: "Biarritz", name: "Biarritz", country: "Francja" },

  // Hiszpania
  { code: "MAD", city: "Madryt", name: "Barajas", country: "Hiszpania" },
  { code: "BCN", city: "Barcelona", name: "El Prat", country: "Hiszpania" },
  { code: "AGP", city: "Malaga", name: "Costa del Sol", country: "Hiszpania" },
  { code: "PMI", city: "Palma de Mallorca", name: "Son Sant Joan", country: "Hiszpania" },
  { code: "IBZ", city: "Ibiza", name: "Ibiza", country: "Hiszpania" },
  { code: "MAH", city: "Minorka", name: "Mahón", country: "Hiszpania" },
  { code: "VLC", city: "Walencja", name: "Manises", country: "Hiszpania" },
  { code: "ALC", city: "Alicante", name: "Elche", country: "Hiszpania" },
  { code: "SVQ", city: "Sewilla", name: "San Pablo", country: "Hiszpania" },
  { code: "BIO", city: "Bilbao", name: "Bilbao", country: "Hiszpania" },
  { code: "GRO", city: "Girona", name: "Costa Brava", country: "Hiszpania" },
  { code: "REU", city: "Reus", name: "Reus", country: "Hiszpania" },
  { code: "TCI", city: "Teneryfa", name: "Wszystkie lotniska", country: "Hiszpania", isMetro: true, members: ["TFN","TFS"], aliases: ["Tenerife"] },
  { code: "TFS", city: "Teneryfa", name: "Teneryfa Południe", country: "Hiszpania" },
  { code: "TFN", city: "Teneryfa", name: "Teneryfa Północ", country: "Hiszpania" },
  { code: "LPA", city: "Gran Canaria", name: "Las Palmas", country: "Hiszpania" },
  { code: "ACE", city: "Lanzarote", name: "Arrecife", country: "Hiszpania" },
  { code: "FUE", city: "Fuerteventura", name: "Fuerteventura", country: "Hiszpania" },
  { code: "SCQ", city: "Santiago de Compostela", name: "Santiago", country: "Hiszpania" },

  // Portugalia
  { code: "LIS", city: "Lizbona", name: "Humberto Delgado", country: "Portugalia" },
  { code: "OPO", city: "Porto", name: "Francisco Sá Carneiro", country: "Portugalia" },
  { code: "FAO", city: "Faro", name: "Algarve", country: "Portugalia" },
  { code: "FNC", city: "Madera", name: "Funchal", country: "Portugalia" },
  { code: "PDL", city: "Azory", name: "Ponta Delgada", country: "Portugalia" },

  // Włochy
  { code: "ROM", city: "Rzym", name: "Wszystkie lotniska", country: "Włochy", isMetro: true, members: ["FCO","CIA"], aliases: ["Rome", "Roma"] },
  { code: "FCO", city: "Rzym", name: "Fiumicino", country: "Włochy" },
  { code: "CIA", city: "Rzym", name: "Ciampino", country: "Włochy" },
  { code: "MIL", city: "Mediolan", name: "Wszystkie lotniska", country: "Włochy", isMetro: true, members: ["MXP","LIN","BGY"], aliases: ["Milan", "Milano"] },
  { code: "MXP", city: "Mediolan", name: "Malpensa", country: "Włochy" },
  { code: "LIN", city: "Mediolan", name: "Linate", country: "Włochy" },
  { code: "BGY", city: "Mediolan", name: "Bergamo Orio al Serio", country: "Włochy" },
  { code: "VEN", city: "Wenecja", name: "Wszystkie lotniska", country: "Włochy", isMetro: true, members: ["VCE","TSF"], aliases: ["Venice", "Venezia"] },
  { code: "VCE", city: "Wenecja", name: "Marco Polo", country: "Włochy" },
  { code: "TSF", city: "Wenecja", name: "Treviso", country: "Włochy" },
  { code: "NAP", city: "Neapol", name: "Capodichino", country: "Włochy" },
  { code: "BLQ", city: "Bolonia", name: "Marconi", country: "Włochy" },
  { code: "FLR", city: "Florencja", name: "Peretola", country: "Włochy" },
  { code: "PSA", city: "Piza", name: "Galileo Galilei", country: "Włochy" },
  { code: "TRN", city: "Turyn", name: "Caselle", country: "Włochy" },
  { code: "VRN", city: "Werona", name: "Catullo", country: "Włochy" },
  { code: "BRI", city: "Bari", name: "Karol Wojtyła", country: "Włochy" },
  { code: "CTA", city: "Katania", name: "Fontanarossa", country: "Włochy" },
  { code: "PMO", city: "Palermo", name: "Falcone-Borsellino", country: "Włochy" },
  { code: "OLB", city: "Olbia", name: "Costa Smeralda", country: "Włochy" },
  { code: "CAG", city: "Cagliari", name: "Elmas", country: "Włochy" },
  { code: "AHO", city: "Alghero", name: "Alghero", country: "Włochy" },
  { code: "BDS", city: "Brindisi", name: "Brindisi", country: "Włochy" },

  // Niemcy
  { code: "FRA", city: "Frankfurt", name: "Frankfurt", country: "Niemcy" },
  { code: "MUC", city: "Monachium", name: "Monachium", country: "Niemcy" },
  { code: "BER", city: "Berlin", name: "Brandenburg", country: "Niemcy" },
  { code: "HAM", city: "Hamburg", name: "Hamburg", country: "Niemcy" },
  { code: "DUS", city: "Düsseldorf", name: "Düsseldorf", country: "Niemcy" },
  { code: "CGN", city: "Kolonia", name: "Köln/Bonn", country: "Niemcy" },
  { code: "STR", city: "Stuttgart", name: "Stuttgart", country: "Niemcy" },
  { code: "NUE", city: "Norymberga", name: "Nürnberg", country: "Niemcy" },
  { code: "HAJ", city: "Hanower", name: "Hannover", country: "Niemcy" },
  { code: "DTM", city: "Dortmund", name: "Dortmund", country: "Niemcy" },
  { code: "LEJ", city: "Lipsk", name: "Halle/Leipzig", country: "Niemcy" },
  { code: "FMM", city: "Memmingen", name: "Memmingen", country: "Niemcy" },

  // Austria
  { code: "VIE", city: "Wiedeń", name: "Schwechat", country: "Austria" },
  { code: "SZG", city: "Salzburg", name: "Salzburg", country: "Austria" },
  { code: "INN", city: "Innsbruck", name: "Innsbruck", country: "Austria" },
  { code: "GRZ", city: "Graz", name: "Graz", country: "Austria" },
  { code: "KLU", city: "Klagenfurt", name: "Klagenfurt", country: "Austria" },

  // Szwajcaria
  { code: "ZRH", city: "Zurych", name: "Kloten", country: "Szwajcaria" },
  { code: "GVA", city: "Genewa", name: "Cointrin", country: "Szwajcaria" },
  { code: "BSL", city: "Bazylea", name: "EuroAirport", country: "Szwajcaria" },

  // Holandia
  { code: "AMS", city: "Amsterdam", name: "Schiphol", country: "Holandia" },
  { code: "EIN", city: "Eindhoven", name: "Eindhoven", country: "Holandia" },
  { code: "RTM", city: "Rotterdam", name: "The Hague", country: "Holandia" },

  // Belgia
  { code: "BRU", city: "Bruksela", name: "Zaventem", country: "Belgia" },
  { code: "CRL", city: "Charleroi", name: "Brussels South", country: "Belgia" },
  { code: "ANR", city: "Antwerpia", name: "Deurne", country: "Belgia" },

  // Luksemburg
  { code: "LUX", city: "Luksemburg", name: "Findel", country: "Luksemburg" },

  // Dania
  { code: "CPH", city: "Kopenhaga", name: "Kastrup", country: "Dania" },
  { code: "BLL", city: "Billund", name: "Billund", country: "Dania" },
  { code: "AAL", city: "Aalborg", name: "Aalborg", country: "Dania" },

  // Szwecja
  { code: "STO", city: "Sztokholm", name: "Wszystkie lotniska", country: "Szwecja", isMetro: true, members: ["ARN","NYO"], aliases: ["Stockholm"] },
  { code: "ARN", city: "Sztokholm", name: "Arlanda", country: "Szwecja" },
  { code: "NYO", city: "Sztokholm", name: "Skavsta", country: "Szwecja" },
  { code: "GOT", city: "Göteborg", name: "Landvetter", country: "Szwecja" },
  { code: "MMX", city: "Malmö", name: "Malmö", country: "Szwecja" },

  // Norwegia
  { code: "OSE", city: "Oslo", name: "Wszystkie lotniska", country: "Norwegia", isMetro: true, members: ["OSL","TRF"], aliases: ["Oslo"] },
  { code: "OSL", city: "Oslo", name: "Gardermoen", country: "Norwegia" },
  { code: "TRF", city: "Oslo", name: "Torp Sandefjord", country: "Norwegia" },
  { code: "BGO", city: "Bergen", name: "Flesland", country: "Norwegia" },
  { code: "TRD", city: "Trondheim", name: "Værnes", country: "Norwegia" },
  { code: "SVG", city: "Stavanger", name: "Sola", country: "Norwegia" },

  // Finlandia
  { code: "HEL", city: "Helsinki", name: "Vantaa", country: "Finlandia" },
  { code: "TMP", city: "Tampere", name: "Tampere-Pirkkala", country: "Finlandia" },
  { code: "RVN", city: "Rovaniemi", name: "Rovaniemi", country: "Finlandia" },

  // Islandia
  { code: "KEF", city: "Reykjavik", name: "Keflavik", country: "Islandia" },

  // Czechy / Słowacja / Węgry
  { code: "PRG", city: "Praga", name: "Václav Havel", country: "Czechy" },
  { code: "BRQ", city: "Brno", name: "Brno", country: "Czechy" },
  { code: "OSR", city: "Ostrawa", name: "Leoš Janáček", country: "Czechy" },
  { code: "BTS", city: "Bratysława", name: "Bratysława", country: "Słowacja" },
  { code: "KSC", city: "Koszyce", name: "Košice", country: "Słowacja" },
  { code: "BUD", city: "Budapeszt", name: "Ferenc Liszt", country: "Węgry" },
  { code: "DEB", city: "Debreczyn", name: "Debrecen", country: "Węgry" },

  // Rumunia / Bułgaria
  { code: "OTP", city: "Bukareszt", name: "Henri Coandă", country: "Rumunia" },
  { code: "CLJ", city: "Kluż-Napoka", name: "Cluj", country: "Rumunia" },
  { code: "TSR", city: "Timișoara", name: "Traian Vuia", country: "Rumunia" },
  { code: "SOF", city: "Sofia", name: "Sofia", country: "Bułgaria" },
  { code: "VAR", city: "Warna", name: "Warna", country: "Bułgaria" },
  { code: "BOJ", city: "Burgas", name: "Burgas", country: "Bułgaria" },

  // Bałkany
  { code: "ZAG", city: "Zagrzeb", name: "Pleso", country: "Chorwacja" },
  { code: "SPU", city: "Split", name: "Split", country: "Chorwacja" },
  { code: "DBV", city: "Dubrownik", name: "Dubrovnik", country: "Chorwacja" },
  { code: "ZAD", city: "Zadar", name: "Zadar", country: "Chorwacja" },
  { code: "PUY", city: "Pula", name: "Pula", country: "Chorwacja" },
  { code: "RJK", city: "Rijeka", name: "Rijeka", country: "Chorwacja" },
  { code: "LJU", city: "Lublana", name: "Jože Pučnik", country: "Słowenia" },
  { code: "BEG", city: "Belgrad", name: "Nikola Tesla", country: "Serbia" },
  { code: "INI", city: "Nisz", name: "Konstantin Veliki", country: "Serbia" },
  { code: "SJJ", city: "Sarajewo", name: "Sarajevo", country: "Bośnia i Hercegowina" },
  { code: "TIV", city: "Tivat", name: "Tivat", country: "Czarnogóra" },
  { code: "TGD", city: "Podgorica", name: "Podgorica", country: "Czarnogóra" },
  { code: "TIA", city: "Tirana", name: "Nënë Tereza", country: "Albania" },
  { code: "SKP", city: "Skopje", name: "Skopje", country: "Macedonia Północna" },
  { code: "OHD", city: "Ochryda", name: "Ohrid", country: "Macedonia Północna" },

  // Bałtyckie
  { code: "RIX", city: "Ryga", name: "Ryga", country: "Łotwa" },
  { code: "VNO", city: "Wilno", name: "Wilno", country: "Litwa" },
  { code: "KUN", city: "Kowno", name: "Kowno", country: "Litwa" },
  { code: "PLQ", city: "Połąga", name: "Palanga", country: "Litwa" },
  { code: "TLL", city: "Tallinn", name: "Lennart Meri", country: "Estonia" },

  // Grecja
  { code: "ATH", city: "Ateny", name: "Eleftherios Venizelos", country: "Grecja" },
  { code: "SKG", city: "Saloniki", name: "Makedonia", country: "Grecja" },
  { code: "HER", city: "Heraklion", name: "Nikos Kazantzakis", country: "Grecja" },
  { code: "CHQ", city: "Chania", name: "Chania", country: "Grecja" },
  { code: "RHO", city: "Rodos", name: "Diagoras", country: "Grecja" },
  { code: "KGS", city: "Kos", name: "Hippokrates", country: "Grecja" },
  { code: "JTR", city: "Santorini", name: "Santorini", country: "Grecja" },
  { code: "JMK", city: "Mykonos", name: "Mykonos", country: "Grecja" },
  { code: "CFU", city: "Korfu", name: "Ioannis Kapodistrias", country: "Grecja" },
  { code: "ZTH", city: "Zakynthos", name: "Zakynthos", country: "Grecja" },
  { code: "EFL", city: "Kefalonia", name: "Kefalonia", country: "Grecja" },
  { code: "KLX", city: "Kalamata", name: "Kalamata", country: "Grecja" },
  { code: "PVK", city: "Preveza", name: "Aktion", country: "Grecja" },
  { code: "JSI", city: "Skiathos", name: "Skiathos", country: "Grecja" },
  { code: "MJT", city: "Lesbos", name: "Mytilene", country: "Grecja" },

  // Cypr / Malta
  { code: "LCA", city: "Larnaka", name: "Larnaca", country: "Cypr" },
  { code: "PFO", city: "Pafos", name: "Paphos", country: "Cypr" },
  { code: "MLA", city: "Malta", name: "Luqa", country: "Malta" },

  // Turcja
  { code: "ISA", city: "Stambuł", name: "Wszystkie lotniska", country: "Turcja", isMetro: true, members: ["IST","SAW"], aliases: ["Istanbul"] },
  { code: "IST", city: "Stambuł", name: "Istanbul", country: "Turcja" },
  { code: "SAW", city: "Stambuł", name: "Sabiha Gökçen", country: "Turcja" },
  { code: "AYT", city: "Antalya", name: "Antalya", country: "Turcja" },
  { code: "ESB", city: "Ankara", name: "Esenboğa", country: "Turcja" },
  { code: "ADB", city: "Izmir", name: "Adnan Menderes", country: "Turcja" },
  { code: "BJV", city: "Bodrum", name: "Milas-Bodrum", country: "Turcja" },
  { code: "DLM", city: "Dalaman", name: "Dalaman", country: "Turcja" },
  { code: "ASR", city: "Kayseri", name: "Erkilet", country: "Turcja" },
  { code: "TZX", city: "Trabzon", name: "Trabzon", country: "Turcja" },

  // Bliski Wschód
  { code: "DXA", city: "Dubaj", name: "Wszystkie lotniska", country: "ZEA", isMetro: true, members: ["DXB","DWC"], aliases: ["Dubai"] },
  { code: "DXB", city: "Dubaj", name: "Dubai Intl", country: "ZEA" },
  { code: "DWC", city: "Dubaj", name: "Al Maktoum", country: "ZEA" },
  { code: "AUH", city: "Abu Zabi", name: "Abu Dhabi Intl", country: "ZEA" },
  { code: "DOH", city: "Doha", name: "Hamad", country: "Katar" },
  { code: "RUH", city: "Rijad", name: "King Khalid", country: "Arabia Saudyjska" },
  { code: "JED", city: "Dżudda", name: "King Abdulaziz", country: "Arabia Saudyjska" },
  { code: "AMM", city: "Amman", name: "Queen Alia", country: "Jordania" },
  { code: "BEY", city: "Bejrut", name: "Rafic Hariri", country: "Liban" },
  { code: "TLV", city: "Tel Awiw", name: "Ben Gurion", country: "Izrael" },
  { code: "ETM", city: "Ejlat", name: "Ramon", country: "Izrael" },
  { code: "KWI", city: "Kuwejt", name: "Kuwait", country: "Kuwejt" },
  { code: "BAH", city: "Manama", name: "Bahrain", country: "Bahrajn" },
  { code: "MCT", city: "Maskat", name: "Muscat", country: "Oman" },

  // Egipt / Afryka Płn.
  { code: "CAI", city: "Kair", name: "Kair", country: "Egipt" },
  { code: "HRG", city: "Hurghada", name: "Hurghada", country: "Egipt" },
  { code: "SSH", city: "Sharm el-Sheikh", name: "Sharm el-Sheikh", country: "Egipt" },
  { code: "RMF", city: "Marsa Alam", name: "Marsa Alam", country: "Egipt" },
  { code: "TUN", city: "Tunis", name: "Carthage", country: "Tunezja" },
  { code: "DJE", city: "Dżerba", name: "Zarzis", country: "Tunezja" },
  { code: "MIR", city: "Monastyr", name: "Habib Bourguiba", country: "Tunezja" },
  { code: "RAK", city: "Marrakesz", name: "Menara", country: "Maroko" },
  { code: "AGA", city: "Agadir", name: "Al Massira", country: "Maroko" },
  { code: "CMN", city: "Casablanca", name: "Mohammed V", country: "Maroko" },
  { code: "FEZ", city: "Fez", name: "Saïss", country: "Maroko" },
  { code: "TNG", city: "Tanger", name: "Ibn Battuta", country: "Maroko" },

  // Afryka Subsaharyjska
  { code: "JNB", city: "Johannesburg", name: "OR Tambo", country: "RPA" },
  { code: "CPT", city: "Kapsztad", name: "Cape Town", country: "RPA" },
  { code: "NBO", city: "Nairobi", name: "Jomo Kenyatta", country: "Kenia" },
  { code: "MBA", city: "Mombasa", name: "Moi", country: "Kenia" },
  { code: "ZNZ", city: "Zanzibar", name: "Abeid Karume", country: "Tanzania" },
  { code: "JRO", city: "Kilimandżaro", name: "Kilimanjaro", country: "Tanzania" },
  { code: "ADD", city: "Addis Abeba", name: "Bole", country: "Etiopia" },
  { code: "MRU", city: "Mauritius", name: "SSR Intl", country: "Mauritius" },
  { code: "SEZ", city: "Seszele", name: "Mahé", country: "Seszele" },

  // CIS / Kaukaz
  { code: "GYD", city: "Baku", name: "Heydar Aliyev", country: "Azerbejdżan" },
  { code: "EVN", city: "Erywań", name: "Zvartnots", country: "Armenia" },
  { code: "TBS", city: "Tbilisi", name: "Tbilisi", country: "Gruzja" },

  // Daleki Wschód
  { code: "BKA", city: "Bangkok", name: "Wszystkie lotniska", country: "Tajlandia", isMetro: true, members: ["BKK","DMK"], aliases: ["Bangkok"] },
  { code: "BKK", city: "Bangkok", name: "Suvarnabhumi", country: "Tajlandia" },
  { code: "DMK", city: "Bangkok", name: "Don Mueang", country: "Tajlandia" },
  { code: "HKT", city: "Phuket", name: "Phuket", country: "Tajlandia" },
  { code: "USM", city: "Koh Samui", name: "Samui", country: "Tajlandia" },
  { code: "CNX", city: "Chiang Mai", name: "Chiang Mai", country: "Tajlandia" },
  { code: "KBV", city: "Krabi", name: "Krabi", country: "Tajlandia" },
  { code: "REP", city: "Siem Reap", name: "Angkor", country: "Kambodża" },
  { code: "PNH", city: "Phnom Penh", name: "Phnom Penh", country: "Kambodża" },
  { code: "SGN", city: "Ho Chi Minh", name: "Tan Son Nhat", country: "Wietnam" },
  { code: "HAN", city: "Hanoi", name: "Noi Bai", country: "Wietnam" },
  { code: "DAD", city: "Da Nang", name: "Da Nang", country: "Wietnam" },
  { code: "KUL", city: "Kuala Lumpur", name: "KLIA", country: "Malezja" },
  { code: "PEN", city: "Penang", name: "Penang", country: "Malezja" },
  { code: "LGK", city: "Langkawi", name: "Langkawi", country: "Malezja" },
  { code: "SIN", city: "Singapur", name: "Changi", country: "Singapur" },
  { code: "DPS", city: "Bali", name: "Ngurah Rai", country: "Indonezja" },
  { code: "CGK", city: "Dżakarta", name: "Soekarno-Hatta", country: "Indonezja" },
  { code: "HKG", city: "Hongkong", name: "Hong Kong Intl", country: "Hongkong" },
  { code: "TPE", city: "Tajpej", name: "Taoyuan", country: "Tajwan" },
  { code: "SEL", city: "Seul", name: "Wszystkie lotniska", country: "Korea Płd.", isMetro: true, members: ["ICN","GMP"], aliases: ["Seoul"] },
  { code: "ICN", city: "Seul", name: "Incheon", country: "Korea Płd." },
  { code: "GMP", city: "Seul", name: "Gimpo", country: "Korea Płd." },
  { code: "TYO", city: "Tokio", name: "Wszystkie lotniska", country: "Japonia", isMetro: true, members: ["NRT","HND"], aliases: ["Tokyo"] },
  { code: "NRT", city: "Tokio", name: "Narita", country: "Japonia" },
  { code: "HND", city: "Tokio", name: "Haneda", country: "Japonia" },
  { code: "OSA", city: "Osaka", name: "Wszystkie lotniska", country: "Japonia", isMetro: true, members: ["KIX","ITM"], aliases: ["Osaka"] },
  { code: "KIX", city: "Osaka", name: "Kansai", country: "Japonia" },
  { code: "ITM", city: "Osaka", name: "Itami", country: "Japonia" },
  { code: "BJS", city: "Pekin", name: "Wszystkie lotniska", country: "Chiny", isMetro: true, members: ["PEK","PKX"], aliases: ["Beijing"] },
  { code: "PEK", city: "Pekin", name: "Capital", country: "Chiny" },
  { code: "PKX", city: "Pekin", name: "Daxing", country: "Chiny" },
  { code: "SHG", city: "Szanghaj", name: "Wszystkie lotniska", country: "Chiny", isMetro: true, members: ["PVG","SHA"], aliases: ["Shanghai"] },
  { code: "PVG", city: "Szanghaj", name: "Pudong", country: "Chiny" },
  { code: "SHA", city: "Szanghaj", name: "Hongqiao", country: "Chiny" },
  { code: "CAN", city: "Kanton", name: "Baiyun", country: "Chiny" },
  { code: "MNL", city: "Manila", name: "Ninoy Aquino", country: "Filipiny" },
  { code: "CEB", city: "Cebu", name: "Mactan-Cebu", country: "Filipiny" },
  { code: "MLE", city: "Male", name: "Velana", country: "Malediwy" },
  { code: "CMB", city: "Kolombo", name: "Bandaranaike", country: "Sri Lanka" },
  { code: "DEL", city: "Delhi", name: "Indira Gandhi", country: "Indie" },
  { code: "BOM", city: "Mumbaj", name: "Chhatrapati Shivaji", country: "Indie" },
  { code: "GOI", city: "Goa", name: "Goa Dabolim", country: "Indie" },
  { code: "BLR", city: "Bengaluru", name: "Kempegowda", country: "Indie" },
  { code: "MAA", city: "Chennai", name: "Chennai", country: "Indie" },
  { code: "KTM", city: "Katmandu", name: "Tribhuvan", country: "Nepal" },

  // Australia / Oceania
  { code: "SYD", city: "Sydney", name: "Kingsford Smith", country: "Australia" },
  { code: "MEL", city: "Melbourne", name: "Tullamarine", country: "Australia" },
  { code: "BNE", city: "Brisbane", name: "Brisbane", country: "Australia" },
  { code: "PER", city: "Perth", name: "Perth", country: "Australia" },
  { code: "AKL", city: "Auckland", name: "Auckland", country: "Nowa Zelandia" },
  { code: "NAN", city: "Nadi", name: "Nadi", country: "Fidżi" },

  // Ameryka Płn.
  { code: "NYC", city: "Nowy Jork", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["JFK","EWR","LGA"], aliases: ["New York", "Nowy York", "NewYork"] },
  { code: "JFK", city: "Nowy Jork", name: "John F. Kennedy", country: "USA" },
  { code: "EWR", city: "Nowy Jork", name: "Newark", country: "USA" },
  { code: "LGA", city: "Nowy Jork", name: "LaGuardia", country: "USA" },
  { code: "BOS", city: "Boston", name: "Logan", country: "USA" },
  { code: "CHI", city: "Chicago", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["ORD","MDW"], aliases: ["Chicago"] },
  { code: "ORD", city: "Chicago", name: "O'Hare", country: "USA" },
  { code: "MDW", city: "Chicago", name: "Midway", country: "USA" },
  { code: "MIA", city: "Miami", name: "Miami Intl", country: "USA" },
  { code: "FLL", city: "Fort Lauderdale", name: "Hollywood", country: "USA" },
  { code: "MCO", city: "Orlando", name: "Orlando Intl", country: "USA" },
  { code: "LAA", city: "Los Angeles", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["LAX","BUR","LGB","SNA","ONT"], aliases: ["Los Angeles", "LA"] },
  { code: "LAX", city: "Los Angeles", name: "LAX", country: "USA" },
  { code: "BUR", city: "Los Angeles", name: "Hollywood Burbank", country: "USA" },
  { code: "LGB", city: "Los Angeles", name: "Long Beach", country: "USA" },
  { code: "SNA", city: "Los Angeles", name: "John Wayne (Orange County)", country: "USA" },
  { code: "ONT", city: "Los Angeles", name: "Ontario", country: "USA" },
  { code: "SFA", city: "San Francisco", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["SFO","OAK","SJC"], aliases: ["San Francisco", "SF Bay"] },
  { code: "SFO", city: "San Francisco", name: "SFO", country: "USA" },
  { code: "OAK", city: "San Francisco", name: "Oakland", country: "USA" },
  { code: "SJC", city: "San Francisco", name: "San Jose", country: "USA" },
  { code: "LAS", city: "Las Vegas", name: "Harry Reid", country: "USA" },
  { code: "SEA", city: "Seattle", name: "Tacoma", country: "USA" },
  { code: "WAS", city: "Waszyngton", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["IAD","DCA","BWI"], aliases: ["Washington"] },
  { code: "IAD", city: "Waszyngton", name: "Dulles", country: "USA" },
  { code: "DCA", city: "Waszyngton", name: "Reagan", country: "USA" },
  { code: "BWI", city: "Waszyngton", name: "Baltimore/Washington", country: "USA" },
  { code: "HOA", city: "Houston", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["IAH","HOU"], aliases: ["Houston"] },
  { code: "IAH", city: "Houston", name: "George Bush", country: "USA" },
  { code: "HOU", city: "Houston", name: "Hobby", country: "USA" },
  { code: "DFA", city: "Dallas", name: "Wszystkie lotniska", country: "USA", isMetro: true, members: ["DFW","DAL"], aliases: ["Dallas", "Dallas Fort Worth"] },
  { code: "DFW", city: "Dallas", name: "Fort Worth", country: "USA" },
  { code: "DAL", city: "Dallas", name: "Love Field", country: "USA" },
  { code: "ATL", city: "Atlanta", name: "Hartsfield-Jackson", country: "USA" },
  { code: "PHL", city: "Filadelfia", name: "PHL", country: "USA" },
  { code: "DEN", city: "Denver", name: "Denver Intl", country: "USA" },
  { code: "PHX", city: "Phoenix", name: "Sky Harbor", country: "USA" },
  { code: "YTO", city: "Toronto", name: "Wszystkie lotniska", country: "Kanada", isMetro: true, members: ["YYZ","YTZ"], aliases: ["Toronto"] },
  { code: "YYZ", city: "Toronto", name: "Pearson", country: "Kanada" },
  { code: "YTZ", city: "Toronto", name: "Billy Bishop", country: "Kanada" },
  { code: "YUL", city: "Montreal", name: "Trudeau", country: "Kanada" },
  { code: "YVR", city: "Vancouver", name: "Vancouver", country: "Kanada" },

  // Karaiby / Ameryka Środk.
  { code: "CUN", city: "Cancún", name: "Cancún Intl", country: "Meksyk" },
  { code: "MEX", city: "Meksyk", name: "Benito Juárez", country: "Meksyk" },
  { code: "PVR", city: "Puerto Vallarta", name: "Gustavo Díaz", country: "Meksyk" },
  { code: "SJD", city: "Los Cabos", name: "Los Cabos", country: "Meksyk" },
  { code: "PUJ", city: "Punta Cana", name: "Punta Cana", country: "Dominikana" },
  { code: "STI", city: "Santiago de los Caballeros", name: "Cibao", country: "Dominikana" },
  { code: "HAV", city: "Hawana", name: "José Martí", country: "Kuba" },
  { code: "VRA", city: "Varadero", name: "Juan Gualberto Gómez", country: "Kuba" },
  { code: "MBJ", city: "Montego Bay", name: "Sangster", country: "Jamajka" },
  { code: "NAS", city: "Nassau", name: "Lynden Pindling", country: "Bahamy" },
  { code: "BGI", city: "Bridgetown", name: "Grantley Adams", country: "Barbados" },

  // Ameryka Płd.
  { code: "SAO", city: "São Paulo", name: "Wszystkie lotniska", country: "Brazylia", isMetro: true, members: ["GRU","CGH","VCP"], aliases: ["Sao Paulo"] },
  { code: "GRU", city: "São Paulo", name: "Guarulhos", country: "Brazylia" },
  { code: "CGH", city: "São Paulo", name: "Congonhas", country: "Brazylia" },
  { code: "VCP", city: "São Paulo", name: "Viracopos", country: "Brazylia" },
  { code: "RIO", city: "Rio de Janeiro", name: "Wszystkie lotniska", country: "Brazylia", isMetro: true, members: ["GIG","SDU"], aliases: ["Rio"] },
  { code: "GIG", city: "Rio de Janeiro", name: "Galeão", country: "Brazylia" },
  { code: "SDU", city: "Rio de Janeiro", name: "Santos Dumont", country: "Brazylia" },
  { code: "BUE", city: "Buenos Aires", name: "Wszystkie lotniska", country: "Argentyna", isMetro: true, members: ["EZE","AEP"], aliases: ["Buenos Aires"] },
  { code: "EZE", city: "Buenos Aires", name: "Ezeiza", country: "Argentyna" },
  { code: "AEP", city: "Buenos Aires", name: "Aeroparque Jorge Newbery", country: "Argentyna" },
  { code: "SCL", city: "Santiago", name: "Arturo Merino", country: "Chile" },
  { code: "LIM", city: "Lima", name: "Jorge Chávez", country: "Peru" },
  { code: "BOG", city: "Bogota", name: "El Dorado", country: "Kolumbia" },
  { code: "UIO", city: "Quito", name: "Mariscal Sucre", country: "Ekwador" },
  { code: "MVD", city: "Montevideo", name: "Carrasco", country: "Urugwaj" },
];

// ============================================================================
// WPISY „CAŁY KRAJ" (XPL, XGB, XFR…)
// ----------------------------------------------------------------------------
// Syntetyczne kody 3-literowe z prefixem `X` (nie kolidują z IATA, które
// nie używa X-prefixu w komercyjnym pasażerskim ruchu). Generowane raz
// przy module-load z listy AIRPORTS. Backend traktuje je jak metro:
// `expandMetro(XPL)` → wszystkie polskie lotniska → fan-out do eSky.
// ============================================================================

const COUNTRY_CODES: Record<string, string> = {
  "Polska": "XPL",
  "Wielka Brytania": "XGB",
  "Irlandia": "XIE",
  "Francja": "XFR",
  "Hiszpania": "XES",
  "Portugalia": "XPT",
  "Włochy": "XIT",
  "Niemcy": "XDE",
  "Austria": "XAT",
  "Szwajcaria": "XCH",
  "Holandia": "XNL",
  "Belgia": "XBE",
  "Luksemburg": "XLU",
  "Dania": "XDK",
  "Szwecja": "XSE",
  "Norwegia": "XNO",
  "Finlandia": "XFI",
  "Islandia": "XIS",
  "Czechy": "XCZ",
  "Słowacja": "XSK",
  "Węgry": "XHU",
  "Rumunia": "XRO",
  "Bułgaria": "XBG",
  "Chorwacja": "XHR",
  "Słowenia": "XSI",
  "Serbia": "XRS",
  "Bośnia i Hercegowina": "XBA",
  "Czarnogóra": "XME",
  "Albania": "XAL",
  "Macedonia Północna": "XMK",
  "Łotwa": "XLV",
  "Litwa": "XLT",
  "Estonia": "XEE",
  "Grecja": "XGR",
  "Cypr": "XCY",
  "Malta": "XMT",
  "Turcja": "XTR",
  "ZEA": "XAE",
  "Katar": "XQA",
  "Arabia Saudyjska": "XSA",
  "Jordania": "XJO",
  "Liban": "XLB",
  "Izrael": "XIL",
  "Kuwejt": "XKW",
  "Bahrajn": "XBH",
  "Oman": "XOM",
  "Egipt": "XEG",
  "Tunezja": "XTN",
  "Maroko": "XMA",
  "RPA": "XZA",
  "Kenia": "XKE",
  "Tanzania": "XTZ",
  "Etiopia": "XET",
  "Mauritius": "XMU",
  "Seszele": "XSC",
  "Azerbejdżan": "XAZ",
  "Armenia": "XAM",
  "Gruzja": "XGE",
  "Tajlandia": "XTH",
  "Kambodża": "XKH",
  "Wietnam": "XVN",
  "Malezja": "XMY",
  "Singapur": "XSG",
  "Indonezja": "XID",
  "Hongkong": "XHK",
  "Tajwan": "XTW",
  "Korea Płd.": "XKR",
  "Japonia": "XJP",
  "Chiny": "XCN",
  "Filipiny": "XPH",
  "Malediwy": "XMV",
  "Sri Lanka": "XLK",
  "Indie": "XIN",
  "Nepal": "XNP",
  "Australia": "XAU",
  "Nowa Zelandia": "XNZ",
  "Fidżi": "XFJ",
  "USA": "XUS",
  "Kanada": "XCA",
  "Meksyk": "XMX",
  "Dominikana": "XDO",
  "Kuba": "XCU",
  "Jamajka": "XJM",
  "Bahamy": "XBS",
  "Barbados": "XBB",
  "Brazylia": "XBR",
  "Argentyna": "XAR",
  "Chile": "XCL",
  "Peru": "XPE",
  "Kolumbia": "XCO",
  "Ekwador": "XEC",
  "Urugwaj": "XUY",
};

// Aliasy nazw kraju do wyszukiwania (po angielsku itp.), żeby user wpisał
// „Poland" i znalazł „Polska — cały kraj".
const COUNTRY_ALIASES: Record<string, string[]> = {
  "Polska": ["Poland", "PL"],
  "Wielka Brytania": ["United Kingdom", "UK", "GB", "Anglia", "England", "Britain"],
  "Niemcy": ["Germany", "DE", "Deutschland"],
  "Francja": ["France", "FR"],
  "Hiszpania": ["Spain", "ES", "España"],
  "Włochy": ["Italy", "IT", "Italia"],
  "Portugalia": ["Portugal", "PT"],
  "Holandia": ["Netherlands", "NL", "Nederland"],
  "Czechy": ["Czechia", "CZ", "Czech Republic"],
  "Grecja": ["Greece", "GR"],
  "Chorwacja": ["Croatia", "HR"],
  "Turcja": ["Turkey", "TR", "Türkiye"],
  "USA": ["United States", "US", "Stany Zjednoczone", "America"],
  "Wielka Brytania ": ["Great Britain"],
  "Tajlandia": ["Thailand", "TH"],
  "Japonia": ["Japan", "JP"],
  "Egipt": ["Egypt", "EG"],
  "Maroko": ["Morocco", "MA"],
  "Norwegia": ["Norway", "NO"],
  "Szwecja": ["Sweden", "SE"],
  "Dania": ["Denmark", "DK"],
  "Finlandia": ["Finland", "FI"],
  "Austria": ["AT"],
  "Szwajcaria": ["Switzerland", "CH"],
  "Belgia": ["Belgium", "BE"],
  "Irlandia": ["Ireland", "IE"],
};

(function buildCountryEntries() {
  const byCountry = new Map<string, string[]>();
  for (const a of AIRPORTS) {
    if (a.isMetro) continue; // nie wciągamy pseudo-kodów metra
    const arr = byCountry.get(a.country) ?? [];
    arr.push(a.code);
    byCountry.set(a.country, arr);
  }
  for (const [country, members] of byCountry) {
    const code = COUNTRY_CODES[country];
    if (!code) continue;
    if (members.length < 2) continue; // 1 lotnisko = nie ma sensu „cały kraj"
    AIRPORTS.push({
      code,
      city: country,
      name: "Cały kraj",
      country,
      isCountry: true,
      members,
      aliases: COUNTRY_ALIASES[country],
    });
  }
})();

export function searchAirports(q: string, limit = 12): Airport[] {
  const s = normalizeQuery(q);
  if (!s) {
    // Domyślna lista (puste query): tylko fizyczne polskie lotniska + Berlin.
    // Wpisy metro i „cały kraj" filtrujemy — nie chcemy spamować pickera.
    const polish = AIRPORTS.filter(
      (a) => a.country === "Polska" && !a.isMetro && !a.isCountry,
    );
    const berlin = AIRPORTS.find((a) => a.code === "BER");
    const szzIdx = polish.findIndex((a) => a.code === "SZZ");
    if (berlin && szzIdx !== -1) {
      return [...polish.slice(0, szzIdx + 1), berlin, ...polish.slice(szzIdx + 1)];
    }
    return polish;
  }
  // Pola dopasowania — wszystkie znormalizowane (diakrytyki/spacje out).
  const directMatch = (a: Airport) => {
    const fields = [a.code, a.city, a.country, a.name, ...(a.aliases ?? [])];
    return fields.some((f) => normalizeQuery(f).includes(s));
  };
  const matched = AIRPORTS.filter(directMatch);
  // Doczep metro dla KAŻDEGO dopasowanego portu, nawet jeśli sam wpis metro
  // nie pasuje do query (np. user wpisał „modlin" → pokaż też „Warszawa
  // (wszystkie)" na górze). Dzięki temu grupowanie jest spójne niezależnie
  // od tego, czym user trafił w miasto wieloportowe.
  const codes = new Set(matched.map((a) => a.code));
  const result: Airport[] = [...matched];
  for (const a of matched) {
    if (a.isMetro || a.isCountry) continue;
    const metro = AIRPORTS.find(
      (m) => m.isMetro && m.country === a.country && m.members?.includes(a.code),
    );
    if (metro && !codes.has(metro.code)) {
      result.push(metro);
      codes.add(metro.code);
    }
  }
  // Symetrycznie: gdy trafione zostało metro (np. po aliasie „Warsaw"
  // albo „Paris"), doczep wszystkie jego porty składowe — żeby
  // „Warsaw" i „Warszawa" dawały IDENTYCZNĄ listę z metro + WAW + WMI.
  for (const a of matched) {
    if (!a.isMetro || !a.members) continue;
    for (const code of a.members) {
      if (codes.has(code)) continue;
      const member = AIRPORTS.find((m) => m.code === code);
      if (member) {
        result.push(member);
        codes.add(code);
      }
    }
  }
  // Doczep wpis „cały kraj" gdy user trafił w którekolwiek lotnisko z tego
  // kraju (np. wpisał „Madryt" → pokaż też „Hiszpania (cały kraj)" jako
  // alternatywę). Bez tego user musiałby wiedzieć, że trzeba wpisać nazwę
  // kraju, żeby zobaczyć opcję krajową.
  const countriesMatched = new Set<string>();
  for (const a of matched) {
    if (!a.isMetro && !a.isCountry) countriesMatched.add(a.country);
  }
  for (const country of countriesMatched) {
    const c = AIRPORTS.find((m) => m.isCountry && m.country === country);
    if (c && !codes.has(c.code)) {
      result.push(c);
      codes.add(c.code);
    }
  }
  // Stabilne sortowanie. Klucze (rosnąco):
  //  1) directMatch — bezpośrednie trafienie w query bije auto-doczepione,
  //     żeby np. „londyn" pokazał najpierw LON (metro), a dopiero potem
  //     auto-doczepione „Wielka Brytania (cały kraj)".
  //  2) tier — kraj > metro > zwykłe lotnisko (gdy wszystkie są na tym
  //     samym poziomie bezpośredniości).
  result.sort((a, b) => {
    const ad = directMatch(a) ? 0 : 1;
    const bd = directMatch(b) ? 0 : 1;
    if (ad !== bd) return ad - bd;
    const tier = (x: Airport) => (x.isCountry ? 0 : x.isMetro ? 1 : 2);
    return tier(a) - tier(b);
  });
  return result.slice(0, limit);
}

export function findAirport(code: string): Airport | undefined {
  return AIRPORTS.find((a) => a.code === code);
}

export function airportsByCountry(country: string): Airport[] {
  // Wpisy metro i „cały kraj" pomijamy w listach „lotnisk w kraju" —
  // to nie są fizyczne lotniska, a wpis krajowy duplikowałby się sam ze sobą.
  return AIRPORTS.filter(
    (a) => a.country === country && !a.isMetro && !a.isCountry,
  );
}

/**
 * Wszystkie lotniska tego samego miasta co podany kod (włącznie z nim samym),
 * w kolejności zdefiniowanej w AIRPORTS. Jeśli kod nieznany — pusta tablica.
 * Używane, by w pickerze grupować np. WAW + WMI, LHR/LGW/STN/LTN itd.
 * Wpisy metro / „cały kraj" są pomijane (to nie są fizyczne lotniska).
 */
export function airportsInSameCity(code: string): Airport[] {
  const a = findAirport(code.toUpperCase());
  if (!a) return [];
  return AIRPORTS.filter(
    (x) =>
      !x.isMetro &&
      !x.isCountry &&
      x.city === a.city &&
      x.country === a.country,
  );
}

/**
 * Rozwija kod do listy fizycznych lotnisk. Dla zwykłego lotniska zwraca
 * `[code]`. Dla metro (LON, PAR, NYC…) → wszystkie lotniska miasta.
 * Dla wpisu „cały kraj" (XPL, XGB…) → wszystkie lotniska kraju.
 * Używane przez server function `searchFlights` do fan-outu zapytań do eSky.
 */
export function expandMetro(code: string): string[] {
  const a = AIRPORTS.find((x) => x.code === code.toUpperCase());
  if ((a?.isMetro || a?.isCountry) && a.members?.length) return a.members;
  return [code.toUpperCase()];
}

/**
 * Czy podany kod reprezentuje grupę lotnisk (miasto wieloportowe LON/PAR/NYC…
 * albo cały kraj XPL/XGB/XFR…). Oba przypadki wymagają fan-outu w backendzie.
 */
export function isMetroCode(code: string): boolean {
  const a = AIRPORTS.find((x) => x.code === code.toUpperCase());
  return Boolean(a?.isMetro || a?.isCountry);
}

/** Czy podany kod reprezentuje wpis „cały kraj" (XPL, XGB, XFR…). */
export function isCountryCode(code: string): boolean {
  const a = AIRPORTS.find((x) => x.code === code.toUpperCase());
  return Boolean(a?.isCountry);
}

// Nasze wewnętrzne kody „wszystkie lotniska miasta", których wyszukiwarka
// Aviasales nie zna → prawdziwe kody miast IATA używane przez Aviasales.
// Pozostałe kody metro (LON, PAR, ROM, MIL, NYC…) są poprawne i zostają.
const AVIASALES_CITY_CODE: Record<string, string> = {
  WAR: "WAW", OSE: "OSL", ISA: "IST", DXA: "DXB", BKA: "BKK", SHG: "SHA",
  LAA: "LAX", SFA: "SFO", HOA: "HOU", DFA: "DFW", VEN: "VCE",
};

/**
 * Kod do przekazania wyszukiwarce Aviasales. Zwraca `null` dla „cały kraj"
 * (XGB, XPL…) — Aviasales nie wyszukuje po krajach.
 */
export function toAviasalesCode(code: string): string | null {
  const c = code.toUpperCase();
  if (isCountryCode(c)) return null;
  return AVIASALES_CITY_CODE[c] ?? c;
}

/** Główny kierunek w kraju (miasto/metro z największym ruchem) — zamiast „cały kraj". */
export function mainDestinationInCountry(country: string): string | undefined {
  const list = AIRPORTS.filter((a) => a.country === country && !a.isCountry);
  return (list.find((a) => a.isMetro) ?? list[0])?.code;
}

// Slug helper for country-level SEO pages
const SLUG_MAP: Record<string, string> = {
  "Polska": "polska",
  "Wielka Brytania": "wielka-brytania",
  "Irlandia": "irlandia",
  "Francja": "francja",
  "Hiszpania": "hiszpania",
  "Portugalia": "portugalia",
  "Włochy": "wlochy",
  "Niemcy": "niemcy",
  "Austria": "austria",
  "Szwajcaria": "szwajcaria",
  "Holandia": "holandia",
  "Belgia": "belgia",
  "Luksemburg": "luksemburg",
  "Dania": "dania",
  "Szwecja": "szwecja",
  "Norwegia": "norwegia",
  "Finlandia": "finlandia",
  "Islandia": "islandia",
  "Czechy": "czechy",
  "Słowacja": "slowacja",
  "Węgry": "wegry",
  "Rumunia": "rumunia",
  "Bułgaria": "bulgaria",
  "Chorwacja": "chorwacja",
  "Słowenia": "slowenia",
  "Serbia": "serbia",
  "Bośnia i Hercegowina": "bosnia",
  "Czarnogóra": "czarnogora",
  "Albania": "albania",
  "Macedonia Północna": "macedonia",
  "Łotwa": "lotwa",
  "Litwa": "litwa",
  "Estonia": "estonia",
  "Grecja": "grecja",
  "Cypr": "cypr",
  "Malta": "malta",
  "Turcja": "turcja",
  "ZEA": "zjednoczone-emiraty-arabskie",
  "Katar": "katar",
  "Arabia Saudyjska": "arabia-saudyjska",
  "Jordania": "jordania",
  "Liban": "liban",
  "Izrael": "izrael",
  "Kuwejt": "kuwejt",
  "Bahrajn": "bahrajn",
  "Oman": "oman",
  "Egipt": "egipt",
  "Tunezja": "tunezja",
  "Maroko": "maroko",
  "RPA": "rpa",
  "Kenia": "kenia",
  "Tanzania": "tanzania",
  "Etiopia": "etiopia",
  "Mauritius": "mauritius",
  "Seszele": "seszele",
  "Azerbejdżan": "azerbejdzan",
  "Armenia": "armenia",
  "Gruzja": "gruzja",
  "Tajlandia": "tajlandia",
  "Kambodża": "kambodza",
  "Wietnam": "wietnam",
  "Malezja": "malezja",
  "Singapur": "singapur",
  "Indonezja": "indonezja",
  "Hongkong": "hongkong",
  "Tajwan": "tajwan",
  "Korea Płd.": "korea-poludniowa",
  "Japonia": "japonia",
  "Chiny": "chiny",
  "Filipiny": "filipiny",
  "Malediwy": "malediwy",
  "Sri Lanka": "sri-lanka",
  "Indie": "indie",
  "Nepal": "nepal",
  "Australia": "australia",
  "Nowa Zelandia": "nowa-zelandia",
  "Fidżi": "fidzi",
  "USA": "usa",
  "Kanada": "kanada",
  "Meksyk": "meksyk",
  "Dominikana": "dominikana",
  "Kuba": "kuba",
  "Jamajka": "jamajka",
  "Bahamy": "bahamy",
  "Barbados": "barbados",
  "Brazylia": "brazylia",
  "Argentyna": "argentyna",
  "Chile": "chile",
  "Peru": "peru",
  "Kolumbia": "kolumbia",
  "Ekwador": "ekwador",
  "Urugwaj": "urugwaj",
};

export function countrySlug(country: string): string {
  return SLUG_MAP[country] ?? country.toLowerCase().replace(/\s+/g, "-");
}

export function countryFromSlug(slug: string): string | undefined {
  for (const [name, s] of Object.entries(SLUG_MAP)) {
    if (s === slug) return name;
  }
  return undefined;
}

export function allCountries(): string[] {
  return Array.from(new Set(AIRPORTS.map((a) => a.country))).sort((a, b) =>
    a.localeCompare(b, "pl")
  );
}
